import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ExternalLink,
  Inbox,
  LogOut,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import {
  createProduct,
  deleteContact,
  deleteProduct,
  getCategories,
  getContacts,
  getProducts,
  updateProduct,
} from '../../api';
import { LOGO_URL } from '../../data/content';

const EMPTY_PRODUCT = { category: 'extintores', name: '', description: '', image: '', order: 0 };

const inputClass =
  'bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-amber-400/60';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {type: 'contact'|'product', id, label}

  const loadAll = useCallback(() => {
    getContacts().then(setContacts).catch(() => toast.error('Erro ao carregar contatos'));
    getProducts().then(setProducts).catch(() => toast.error('Erro ao carregar produtos'));
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const openNewProduct = () => {
    setEditing(null);
    setProductForm(EMPTY_PRODUCT);
    setDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setEditing(product);
    setProductForm({
      category: product.category,
      name: product.name,
      description: product.description,
      image: product.image,
      order: product.order,
    });
    setDialogOpen(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await updateProduct(editing.id, productForm);
        toast.success('Produto atualizado!');
      } else {
        await createProduct(productForm);
        toast.success('Produto criado!');
      }
      setDialogOpen(false);
      getProducts().then(setProducts);
    } catch (err) {
      toast.error('Não foi possível salvar o produto');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'contact') {
        await deleteContact(deleteTarget.id);
        setContacts((prev) => prev.filter((c) => c.id !== deleteTarget.id));
        toast.success('Contato removido');
      } else {
        await deleteProduct(deleteTarget.id);
        setProducts((prev) => prev.filter((p) => p.id !== deleteTarget.id));
        toast.success('Produto removido');
      }
    } catch (err) {
      toast.error('Não foi possível remover');
    } finally {
      setDeleteTarget(null);
    }
  };

  const categoryTitle = (slug) =>
    categories.find((c) => c.slug === slug)?.title || slug;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#040D1A] text-slate-100">
      <header className="sticky top-0 z-40 bg-[#040D1A]/90 backdrop-blur-xl border-b border-amber-400/10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logomarca Leão de Fogo" className="w-10 h-10 object-contain" />
            <div>
              <p className="font-chapaza italic font-bold uppercase text-lg text-white leading-none">
                Leão de Fogo
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300/80 mt-1">
                Painel Admin · {user?.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-site-link"
              onClick={() => navigate('/')}
              className="hidden sm:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 hover:text-amber-300 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Ver site
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 border border-red-400/30 text-red-300 font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-full hover:bg-red-400/10 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10">
        <Tabs defaultValue="contatos" className="w-full">
          <TabsList className="grid grid-cols-2 w-full max-w-md mb-8 rounded-full bg-white/5 border border-white/10 p-1 h-auto">
            <TabsTrigger
              value="contatos"
              data-testid="admin-tab-contatos"
              className="rounded-full py-2.5 font-display font-bold uppercase tracking-wide text-xs text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-[#0A2847]"
            >
              <Inbox className="w-4 h-4 mr-2" />
              Contatos ({contacts.length})
            </TabsTrigger>
            <TabsTrigger
              value="produtos"
              data-testid="admin-tab-produtos"
              className="rounded-full py-2.5 font-display font-bold uppercase tracking-wide text-xs text-slate-300 data-[state=active]:bg-amber-400 data-[state=active]:text-[#0A2847]"
            >
              <Package className="w-4 h-4 mr-2" />
              Produtos ({products.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contatos">
            <div className="flex justify-end mb-4">
              <button
                data-testid="admin-refresh-contacts"
                onClick={loadAll}
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-300 hover:text-amber-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Atualizar
              </button>
            </div>
            {contacts.length === 0 ? (
              <p data-testid="contacts-empty" className="text-slate-400 text-sm">
                Nenhum contato recebido ainda.
              </p>
            ) : (
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`contact-card-${c.id}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <p className="font-display font-bold text-white text-lg">{c.name}</p>
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-300 mt-1">
                          {new Date(c.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <button
                        data-testid={`delete-contact-${c.id}`}
                        onClick={() => setDeleteTarget({ type: 'contact', id: c.id, label: c.name })}
                        className="inline-flex items-center gap-1.5 text-red-300 hover:text-red-200 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Excluir
                      </button>
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm text-slate-300">
                      <p><span className="text-slate-500">CPF/CNPJ:</span> {c.cpf_cnpj}</p>
                      <p><span className="text-slate-500">Telefone:</span> {c.phone}</p>
                      <p><span className="text-slate-500">E-mail:</span> {c.email || '—'}</p>
                      <p><span className="text-slate-500">Endereço:</span> {c.address}</p>
                      <p><span className="text-slate-500">Cidade/UF:</span> {c.city}/{c.state}</p>
                      <p><span className="text-slate-500">Serviço:</span> {c.service || '—'}</p>
                    </div>
                    {c.message && (
                      <p className="mt-3 text-sm text-slate-400 border-t border-white/5 pt-3">
                        “{c.message}”
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="produtos">
            <div className="flex justify-end mb-4">
              <button
                data-testid="add-product-button"
                onClick={openNewProduct}
                className="inline-flex items-center gap-2 bg-amber-400 text-[#0A2847] font-display font-bold uppercase tracking-wide text-xs px-5 py-3 rounded-full hover:bg-amber-300 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Novo Produto
              </button>
            </div>
            <div className="space-y-3">
              {products.map((p) => (
                <div
                  key={p.id}
                  data-testid={`product-row-${p.id}`}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                >
                  <div className="w-16 h-16 rounded-xl bg-[#0A2847]/60 flex items-center justify-center shrink-0 overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-white truncate">{p.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300/80 mt-1">
                      {categoryTitle(p.category)}
                    </p>
                  </div>
                  <button
                    data-testid={`edit-product-${p.id}`}
                    onClick={() => openEditProduct(p)}
                    className="inline-flex items-center gap-1.5 text-amber-300 hover:text-amber-200 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    data-testid={`delete-product-${p.id}`}
                    onClick={() => setDeleteTarget({ type: 'product', id: p.id, label: p.name })}
                    className="inline-flex items-center gap-1.5 text-red-300 hover:text-red-200 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="product-dialog"
          className="bg-[#0A2847] border border-amber-400/20 text-white max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-bold uppercase tracking-tight">
              {editing ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveProduct} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">Categoria</Label>
              <Select
                value={productForm.category}
                onValueChange={(v) => setProductForm((prev) => ({ ...prev, category: v }))}
              >
                <SelectTrigger data-testid="product-category-select" className={`${inputClass} mt-2`}>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A2847] border-white/10 text-white">
                  {categories.map((c) => (
                    <SelectItem key={c.slug} value={c.slug}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">Nome</Label>
              <Input
                data-testid="product-name-input"
                value={productForm.name}
                onChange={(e) => setProductForm((prev) => ({ ...prev, name: e.target.value }))}
                required
                className={`${inputClass} mt-2`}
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">Descrição</Label>
              <textarea
                data-testid="product-description-input"
                value={productForm.description}
                onChange={(e) => setProductForm((prev) => ({ ...prev, description: e.target.value }))}
                required
                className="mt-2 w-full min-h-24 rounded-md bg-white/5 border border-white/10 text-white px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/60"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs font-mono uppercase tracking-[0.15em]">URL da Imagem</Label>
              <Input
                data-testid="product-image-input"
                value={productForm.image}
                onChange={(e) => setProductForm((prev) => ({ ...prev, image: e.target.value }))}
                required
                placeholder="https://..."
                className={`${inputClass} mt-2`}
              />
            </div>
            <button
              type="submit"
              data-testid="product-form-submit"
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-400 text-[#0A2847] font-display font-bold uppercase tracking-wide text-sm px-8 py-3.5 rounded-full hover:bg-amber-300 transition-colors disabled:opacity-60"
            >
              {saving ? 'Salvando...' : 'Salvar Produto'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0A2847] border border-red-400/20 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Remover “{deleteTarget?.label}”? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={handleConfirmDelete}
              className="bg-red-500 text-white hover:bg-red-400"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
